import { useEffect } from "react";
import { useToast } from "../../../shared/ui";
import { useViewer } from "../../../state/ViewerProvider";
import { notice, useEditorStore } from "../../../state/editor/useEditor";
import { Icon } from "../../kit";
import { useCaps } from "../CapsProvider";
import { describeImport, onSceneApplied, registerSceneHost } from "./sceneFile";
import { useSceneBusy } from "./useSceneFileActions";

/**
 * Mounted once with the studio chrome (Present and Preview included):
 *
 * - registers this editor as the viewer's host, so the Vault's top-bar Save —
 *   which renders outside the editor — can still save the studio's half;
 * - on v65, puts the studio's half back whenever the viewer applies a scene
 *   the studio did not open itself — above all a "Ring + scene" model, whose
 *   file brought its own look (`sceneApplied`, origin "model");
 * - covers the canvas while a scene file is being opened: the look is being
 *   replaced underneath, and a click then would land on half of it.
 */
export function SceneFileBridge() {
  const { client } = useViewer();
  const store = useEditorStore();
  const caps = useCaps();
  const { toast } = useToast();
  const busy = useSceneBusy();

  useEffect(() => registerSceneHost(client, store), [client, store]);

  useEffect(() => {
    if (!caps.sceneFileV65) return;
    return client.on("sceneApplied", (p) => {
      onSceneApplied(client, store, p).then(
        (how) => {
          if (how !== "applied" || p.origin !== "model") return;
          // The file's own ring: every part is expected to match, so only what did not come back is worth a line.
          toast({ ...describeImport(p.result), title: "Opened with its saved setup" });
        },
        (e: Error) => notice(`Couldn't restore the saved setup: ${e.message}`),
      );
    });
  }, [caps.sceneFileV65, client, store, toast]);

  if (busy !== "open") return null;
  return (
    <div
      className="pointer-events-auto absolute inset-0 z-[140] grid place-items-center bg-white/40"
      role="status"
      aria-live="polite"
      data-scene-opening
    >
      <span className="inline-flex items-center gap-2 rounded-full border border-ed-border bg-ed-panel px-4 py-2 text-[13px] font-medium text-ed-text shadow-[0_6px_20px_rgba(0,0,0,0.08)]">
        <Icon name="progress_activity" size={18} className="animate-spin text-ed-text-2" />
        Opening the scene…
      </span>
    </div>
  );
}
